import { useState, useEffect, useRef } from 'react';
import { CompoundOsReplenishmentPanel } from '../components/products/CompoundOsReplenishmentPanel';
import { LifecycleBadge, LIFECYCLE_OPTIONS } from '../components/products/LifecycleBadge';
import type { ProductLifecycleStatus } from '@bloom/shared';
import { formatCurrency } from '../lib/format';
import { effectiveQualificationQuestions, type ProductQualificationQuestion } from '@bloom/shared';
import { ProductQualificationEditor } from '../components/products/ProductQualificationEditor';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Edit3, Save, X, History, Clock, User,
  Package, DollarSign, Tag, Calendar, FileText,
  ChevronDown, ChevronUp, Plus, AlertTriangle, FlaskConical
} from 'lucide-react';
import { apiClient } from '../lib/api';
import { useUser } from '../store/useAuthStore';
import { RichTextEditor, RichTextDisplay } from '../components/common/RichTextEditor';
import { MySavedSigs } from '../components/products/MySavedSigs';
import { PharmacyBadge } from '../components/pharmacy/PharmacyBadge';
import { resolveFulfillmentPharmacy } from '../lib/pharmacyRouting';
import { formatShortDate, formatDateTime } from '../utils/dateUtils';
import { computeVialMath, formatVialBreakdown } from '../utils/vialMath';

interface Product {
  qualificationQuestions?: ProductQualificationQuestion[];
  _id: string;
  drugName: string;
  concentration: string;
  format: string;
  size: string;
  displayName: string;
  description?: string;
  categories: string[];
  basePrice: number;
  allowPerUnitOrdering: boolean;
  perUnitPrice?: number;
  images: string[];
  estimatedShipDays: number;
  isActive: boolean;
  directToPatient: boolean;
  lowStockWarning?: boolean;
  outOfStock?: boolean;
  lifecycleStatus?: ProductLifecycleStatus;
  productType?: 'medication' | 'supply' | 'lab';
  lab?: {
    panelCode: string;
    cptCode?: string;
    specimenSource?: string;
    requisitionType?: 'at_home_kit' | 'walk_in' | 'mobile_phleb';
    defaultDiagnosisCodes?: string[];
    turnaroundDays?: number;
    biomarkers?: string[];
  };
  requiresTelemedNetwork?: boolean;
  prescriberOptional?: boolean;
  isPeptide?: boolean;
  membershipEligible?: boolean;
  primaryCategorySlug?: string;
  drugFamilySlug?: string;
  // New fields
  budDays?: string;
  defaultSig?: string;
  sigOptions?: string[];
  productDetails?: string;
  createdAt: string;
  updatedAt: string;
  calculatedPrice?: {
    productId: string;
    basePrice: number;
    distributorPrice: number;
    finalPrice: number;
  };
}

interface AuditHistoryEntry {
  id: string;
  action: 'create' | 'update' | 'delete';
  user: {
    id: string;
    email: string;
    role: string;
  };
  timestamp: string;
  changes: {
    field: string;
    oldValue: any;
    newValue: any;
  }[];
  snapshot: Record<string, any>;
}

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useUser();
  const isAdmin = user?.role === 'admin';
  const canManageTelemed = isAdmin && (user as any)?.adminData?.canManageTelemedNetworks === true;

  const [product, setProduct] = useState<Product | null>(null);
  const [history, setHistory] = useState<AuditHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Product>>({});
  const [newCategory, setNewCategory] = useState('');
  const [newSigOption, setNewSigOption] = useState('');
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  // DTP / educational store taxonomy for the admin classification dropdowns
  const [dtpCategories, setDtpCategories] = useState<Array<{ slug: string; displayName: string; parentSlug: string | null }>>([]);
  const [drugFamilies, setDrugFamilies] = useState<Array<{ slug: string; categorySlug: string | null }>>([]);
  const categoryInputRef = useRef<HTMLInputElement>(null);
  const categorySuggestionsRef = useRef<HTMLDivElement>(null);

  // History display state
  const [showHistory, setShowHistory] = useState(false);
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());

  // Removed pricing state - no longer needed since only admins can edit pricing

  useEffect(() => {
    if (id) {
      fetchProduct();
      if (isAdmin) {
        fetchHistory();
        fetchCategories();
      }
    }
  }, [id, isAdmin]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      
      if (user?.role === 'admin' || user?.role === 'distributor' || user?.role === 'supervisor') {
        // Admin/distributor/supervisor fetches product directly (backend applies catalog access control)
        // Non-admin users need includePricing=true to get calculatedPrice from their catalog
        const pricingParam = user?.role !== 'admin' ? '?includePricing=true' : '';
        const response = await apiClient.get<{ data: Product }>(`/products/${id}${pricingParam}`);
        setProduct(response.data);
        setEditForm(response.data);
      } else {
        // Non-admin users: get from aggregated catalog products (same as listing page)
        const catalogResponse = await apiClient.getMyProducts();
        
        if (catalogResponse?.success && catalogResponse.data) {
          const catalogProduct: any = catalogResponse.data.products.find((p: any) => p._id === id);
          
          if (!catalogProduct) {
            setError('Product not found in your assigned catalogs');
            setProduct(null);
            return;
          }
          
          // Format the same way as Products listing page
          const finalPrice = catalogProduct.price || 0;
          const formattedProduct: Product = {
            _id: catalogProduct._id,
            drugName: catalogProduct.drugName,
            concentration: catalogProduct.concentration,
            format: catalogProduct.format,
            size: catalogProduct.size,
            displayName: catalogProduct.displayName,
            description: catalogProduct.description || '',
            categories: catalogProduct.categories || [],
            basePrice: 0, // Not exposed to non-admin users
            allowPerUnitOrdering: catalogProduct.allowPerUnitOrdering || false,
            perUnitPrice: catalogProduct.perUnitPrice || 0,
            images: catalogProduct.images || [],
            estimatedShipDays: catalogProduct.estimatedShipDays || 3,
            isActive: catalogProduct.isActive !== false,
            isPeptide: catalogProduct.isPeptide || false,
            membershipEligible: catalogProduct.membershipEligible || false,
            primaryCategorySlug: catalogProduct.primaryCategorySlug,
            drugFamilySlug: catalogProduct.drugFamilySlug,
            directToPatient: catalogProduct.directToPatient || false,
            createdAt: catalogProduct.createdAt || new Date().toISOString(),
            updatedAt: catalogProduct.updatedAt || new Date().toISOString(),
            calculatedPrice: {
              productId: catalogProduct._id,
              basePrice: 0, // Not exposed to non-admin users
              distributorPrice: finalPrice,
              finalPrice: finalPrice,
            }
          };
          
          setProduct(formattedProduct);
          setEditForm(formattedProduct);
        } else {
          setError(catalogResponse?.error || 'Failed to fetch product');
          setProduct(null);
        }
      }
      
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch product');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await apiClient.get<{ data: { history: AuditHistoryEntry[] } }>(`/products/${id}/history`);
      console.log('History response:', response);
      console.log('History data:', response.data);
      console.log('History array:', response.data.history);
      setHistory(response.data.history);
    } catch (err: any) {
      console.error('Failed to fetch product history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get<{ data: {
        categories: string[];
        formats: string[];
        dtpCategories?: Array<{ slug: string; displayName: string; parentSlug: string | null }>;
        drugFamilies?: Array<{ slug: string; categorySlug: string | null }>;
      } }>('/products/meta/categories');
      setAllCategories(response.data.categories || []);
      setDtpCategories(response.data.dtpCategories || []);
      setDrugFamilies(response.data.drugFamilies || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  // Close category suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        categorySuggestionsRef.current && !categorySuggestionsRef.current.contains(e.target as Node) &&
        categoryInputRef.current && !categoryInputRef.current.contains(e.target as Node)
      ) {
        setShowCategorySuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEdit = () => {
    setIsEditing(true);
    setEditForm(product || {});
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm(product || {});
    setNewCategory('');
  };

  const handleSave = async () => {
    if (!product || !editForm) return;

    const nextBasePrice = parseFloat(editForm.basePrice?.toString() || '0');

    // Negotiated clinic deals are NOT ratcheted the way catalogs are, so raising
    // pharmacy cost can silently push a live deal's house keep negative — every
    // subsequent order under it loses money. Check before saving, not after.
    if (isAdmin && Number.isFinite(nextBasePrice) && nextBasePrice > (product.basePrice || 0)) {
      const res = await apiClient.getClinicDealExposure(id!, nextBasePrice);
      const exposed = ((res as any)?.exposed || res.data?.exposed || []) as Array<{
        dealName: string; accountName: string; newHouseKeep: number;
      }>;
      if (exposed.length > 0) {
        const lines = exposed
          .map(e => `  • ${e.accountName} — "${e.dealName}" → ${formatCurrency(e.newHouseKeep)} per unit`)
          .join('\n');
        const ok = window.confirm(
          `Raising the base price to ${formatCurrency(nextBasePrice)} would make ${exposed.length} ` +
          `active deal${exposed.length === 1 ? '' : 's'} lose money on every order:\n\n${lines}\n\n` +
          `Deal prices are negotiated commitments and will NOT be adjusted automatically. ` +
          `Save anyway?`
        );
        if (!ok) return;
      }
    }

    try {
      setUpdating(true);

      const updateData = {
        ...editForm,
        basePrice: nextBasePrice,
        perUnitPrice: editForm.allowPerUnitOrdering ? parseFloat(editForm.perUnitPrice?.toString() || '0') : undefined,
        estimatedShipDays: parseInt(editForm.estimatedShipDays?.toString() || '3')
      };

      const response = await apiClient.put<{ success: boolean; data: Product; message?: string }>(`/products/${id}`, updateData);
      setProduct(response.data);
      setEditForm(response.data);
      setIsEditing(false);
      
      // Refresh history after update
      if (isAdmin) {
        fetchHistory();
      }
      
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update product');
    } finally {
      setUpdating(false);
    }
  };

  const handleFormChange = (field: keyof Product, value: any) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Human-readable label for a DTP store category slug, e.g. "Peptides › Collagen & skin".
  const dtpCategoryLabel = (slug?: string | null): string => {
    if (!slug) return '';
    const cat = dtpCategories.find(c => c.slug === slug);
    if (!cat) return slug;
    if (cat.parentSlug) {
      const parent = dtpCategories.find(c => c.slug === cat.parentSlug);
      return parent ? `${parent.displayName} › ${cat.displayName}` : cat.displayName;
    }
    return cat.displayName;
  };

  const addCategory = () => {
    if (newCategory.trim() && !editForm.categories?.includes(newCategory.trim())) {
      setEditForm(prev => ({
        ...prev,
        categories: [...(prev.categories || []), newCategory.trim()]
      }));
      setNewCategory('');
    }
  };

  const removeCategory = (category: string) => {
    setEditForm(prev => ({
      ...prev,
      categories: prev.categories?.filter(c => c !== category) || []
    }));
  };

  const moveSigOption = (index: number, direction: 'up' | 'down') => {
    const options = [...(editForm.sigOptions || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= options.length) return;
    [options[index], options[newIndex]] = [options[newIndex], options[index]];
    handleFormChange('sigOptions', options);
    handleFormChange('defaultSig', options[0] || '');
  };

  const filteredCategories = allCategories.filter(
    c => c.toLowerCase().includes(newCategory.toLowerCase()) && !editForm.categories?.includes(c)
  );

  const toggleHistoryEntry = (entryId: string) => {
    setExpandedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'null';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return value.toString();
    return value.toString();
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'create': return 'text-green-600 bg-green-100';
      case 'update': return 'text-blue-600 bg-blue-100';
      case 'delete': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Helper function to get display price (same logic as Products listing page)
  const getDisplayPrice = () => {
    if (!product) return null;

    if (user?.role === 'admin') {
      return product.basePrice;
    } else if (user?.role === 'supervisor' || user?.role === 'distributor') {
      return product.calculatedPrice?.distributorPrice ?? null;
    } else if (user?.role === 'physician' || user?.role === 'physician_readonly') {
      return product.calculatedPrice?.finalPrice ?? null;
    }

    return null;
  };

  // Helper function to get price label
  const getPriceLabel = () => {
    if (user?.role === 'admin') {
      return 'Base Price';
    }
    return 'Your Cost';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !product) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Product</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => navigate('/products')}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
        >
          Back to Products
        </button>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/products')}
                className="text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{product.displayName}</h1>
                <p className="text-gray-600 mt-1">
                  {product.productType === 'lab'
                    ? `Lab panel${product.lab?.panelCode ? ` · ${product.lab.panelCode}` : ''}`
                    : `${product.drugName} - ${product.format}`}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {isAdmin && (
                <>
                  {!isEditing ? (
                    <button
                      onClick={handleEdit}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Edit3 className="w-4 h-4" />
                      <span>Edit Product</span>
                    </button>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={handleCancelEdit}
                        className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <X className="w-4 h-4" />
                        <span>Cancel</span>
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={updating}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {updating ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        <span>Save Changes</span>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Product Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Product Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Drug Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.drugName || ''}
                      onChange={(e) => handleFormChange('drugName', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{product.drugName}</p>
                  )}
                </div>
                
                {/* A lab panel has no strength, form or fill volume. Its identity
                    is the panel code, which is read-only after creation: it is
                    what results are matched back on, so changing it would orphan
                    every requisition already at the vendor. */}
                {product.productType === 'lab' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Panel Code</label>
                      <p className="text-gray-900 py-2 font-mono">{product.lab?.panelCode || '—'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">CPT Code</label>
                      <p className="text-gray-900 py-2">{product.lab?.cptCode || '—'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Specimen Source</label>
                      <p className="text-gray-900 py-2">{product.lab?.specimenSource || '—'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Collection</label>
                      <p className="text-gray-900 py-2">
                        {product.lab?.requisitionType === 'at_home_kit'
                          ? 'At-home kit, mailed to the patient'
                          : product.lab?.requisitionType || '—'}
                      </p>
                    </div>

                  </>
                ) : (
                <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Concentration</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.concentration || ''}
                      onChange={(e) => handleFormChange('concentration', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{product.concentration}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Format</label>
                  {isEditing ? (
                    <select
                      value={editForm.format || ''}
                      onChange={(e) => handleFormChange('format', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="Injectable">Injectable</option>
                      <option value="ODT">ODT</option>
                      <option value="Tablet">Tablet</option>
                      <option value="Capsule">Capsule</option>
                      <option value="Cream">Cream</option>
                      <option value="Gel">Gel</option>
                      <option value="Lozenge">Lozenge</option>
                      <option value="Nasal Spray">Nasal Spray</option>
                    </select>
                  ) : (
                    <p className="text-gray-900 py-2">{product.format}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Size</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.size || ''}
                      onChange={(e) => handleFormChange('size', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{product.size}</p>
                  )}
                </div>
                </>
                )}
              </div>

              {product.productType !== 'lab' && (() => {
                const vm = computeVialMath(isEditing ? { ...product, ...editForm } : product);
                if (!vm.isVial) return null;
                const breakdown = formatVialBreakdown(vm);
                return (
                  <div className="mt-6 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-blue-900">Total mg</div>
                    <div className="mt-1 text-sm text-blue-900">
                      {breakdown ? (
                        <>
                          <span className="font-semibold">{breakdown}</span>
                          <span className="text-blue-700"> — derived from concentration × size</span>
                        </>
                      ) : (
                        <span className="text-blue-700">Could not compute (concentration or size not parseable as mg/mL × mL).</span>
                      )}
                    </div>
                    {isAdmin && vm.perMlMissing && (
                      <div className="mt-2 flex items-start gap-2 text-xs text-amber-800">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>
                          Concentration is missing <code className="font-mono">/mL</code>. Bloom assumes per-mL,
                          but please update the field to read e.g. <code className="font-mono">10 mg/mL</code> so
                          there is no ambiguity for downstream readers.
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Display Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.displayName || ''}
                    onChange={(e) => handleFormChange('displayName', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{product.displayName}</p>
                )}
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                {isEditing ? (
                  <textarea
                    value={editForm.description || ''}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{product.description || 'No description provided'}</p>
                )}
              </div>

              {/* BUD and Default Sig */}
              {product.productType !== 'lab' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">BUD (Beyond Use Date)</label>
                  {isEditing ? (
                    <select
                      value={editForm.budDays || ''}
                      onChange={(e) => handleFormChange('budDays', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select BUD</option>
                      <option value="30 days">30 days</option>
                      <option value="60 days">60 days</option>
                      <option value="90 days">90 days</option>
                      <option value="180 days">180 days</option>
                      <option value="1 year">1 year</option>
                    </select>
                  ) : (
                    <p className="text-gray-900 py-2">{product.budDays || 'Not specified'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sig Options</label>
                  {isEditing ? (
                    <div className="space-y-2">
                      {(editForm.sigOptions || []).map((sig, index) => (
                        <div key={index} className="flex items-center gap-1">
                          <div className="flex flex-col">
                            <button
                              type="button"
                              onClick={() => moveSigOption(index, 'up')}
                              disabled={index === 0}
                              className="text-gray-400 hover:text-gray-600 disabled:opacity-30 p-0.5"
                            >
                              <ChevronUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveSigOption(index, 'down')}
                              disabled={index === (editForm.sigOptions || []).length - 1}
                              className="text-gray-400 hover:text-gray-600 disabled:opacity-30 p-0.5"
                            >
                              <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <span className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                            {index === 0 && <span className="text-xs text-blue-600 font-medium mr-2">(default)</span>}
                            {sig}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (editForm.sigOptions || []).filter((_, i) => i !== index);
                              handleFormChange('sigOptions', updated);
                              handleFormChange('defaultSig', updated[0] || '');
                            }}
                            className="text-gray-400 hover:text-red-500 p-1"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newSigOption}
                          onChange={(e) => setNewSigOption(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newSigOption.trim()) {
                              e.preventDefault();
                              const updated = [...(editForm.sigOptions || []), newSigOption.trim()];
                              handleFormChange('sigOptions', updated);
                              handleFormChange('defaultSig', updated[0]);
                              setNewSigOption('');
                            }
                          }}
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Add sig option, e.g., Take as directed"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (newSigOption.trim()) {
                              const updated = [...(editForm.sigOptions || []), newSigOption.trim()];
                              handleFormChange('sigOptions', updated);
                              handleFormChange('defaultSig', updated[0]);
                              setNewSigOption('');
                            }
                          }}
                          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-1"
                        >
                          <Plus className="w-4 h-4" />
                          Add
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">First option is the default. Physicians can also write custom instructions.</p>
                    </div>
                  ) : (
                    <div>
                      {product.sigOptions && product.sigOptions.length > 0 ? (
                        <ul className="space-y-1">
                          {product.sigOptions.map((sig, index) => (
                            <li key={index} className="text-gray-900 text-sm py-1">
                              {index === 0 && <span className="text-xs text-blue-600 font-medium mr-2">(default)</span>}
                              {sig}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-900 py-2">{product.defaultSig || 'Not specified'}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>

            {/* Physician's own saved sig presets for this product */}
            {user?.role === 'physician' && (
              <MySavedSigs productId={product._id} />
            )}

            {/* Categories */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Categories</h2>
              
              {isEditing ? (
                <div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {editForm.categories?.map(category => (
                      <span
                        key={category}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                      >
                        <Tag className="w-3 h-3" />
                        {category}
                        <button
                          onClick={() => removeCategory(category)}
                          className="hover:text-blue-600 ml-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="relative">
                    <div className="flex gap-2">
                      <input
                        ref={categoryInputRef}
                        type="text"
                        value={newCategory}
                        onChange={(e) => {
                          setNewCategory(e.target.value);
                          setShowCategorySuggestions(true);
                        }}
                        onFocus={() => setShowCategorySuggestions(true)}
                        placeholder="Search or add category"
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addCategory();
                            setShowCategorySuggestions(false);
                          }
                        }}
                      />
                      <button
                        onClick={() => { addCategory(); setShowCategorySuggestions(false); }}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        Add
                      </button>
                    </div>
                    {showCategorySuggestions && filteredCategories.length > 0 && (
                      <div
                        ref={categorySuggestionsRef}
                        className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                      >
                        {filteredCategories.map(cat => (
                          <button
                            key={cat}
                            type="button"
                            onClick={() => {
                              if (!editForm.categories?.includes(cat)) {
                                setEditForm(prev => ({
                                  ...prev,
                                  categories: [...(prev.categories || []), cat]
                                }));
                              }
                              setNewCategory('');
                              setShowCategorySuggestions(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 hover:text-blue-700"
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {product.categories.map(category => (
                    <span
                      key={category}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                    >
                      <Tag className="w-3 h-3" />
                      {category}
                    </span>
                  ))}
                  {product.categories.length === 0 && (
                    <p className="text-gray-500 italic">No categories assigned</p>
                  )}
                </div>
              )}
            </div>

            {/* Biomarkers — lab panels only. Lives in the details area rather
                than the identity grid because it is the substance of what a
                clinic is buying, and reads better full-width. */}
            {product.productType === 'lab' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">
                <FlaskConical className="w-5 h-5 mr-2 text-violet-600" />
                Biomarkers Tested
                {(product.lab?.biomarkers?.length ?? 0) > 0 && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-violet-100 text-violet-800">
                    {product.lab!.biomarkers!.length}
                  </span>
                )}
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                What this panel measures. Reference ranges are not shown here — they
                are personalized to each patient (sex and age) and arrive with the
                result, not the catalog.
              </p>
              {product.lab?.biomarkers && product.lab.biomarkers.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {product.lab.biomarkers.map((bm, i) => (
                    <div
                      key={`${bm}-${i}`}
                      className="flex items-start gap-2 rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-800"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0 mt-1.5" />
                      <span>{bm}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">Not yet listed for this panel.</p>
              )}
            </div>
            )}

            {/* Product Details (Rich Text) */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-gray-600" />
                Product Details
              </h2>

              {isEditing ? (
                <div>
                  <RichTextEditor
                    content={editForm.productDetails || ''}
                    onChange={(html) => handleFormChange('productDetails', html)}
                    placeholder="Add detailed product information (dosing, storage, etc.)..."
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Add detailed product information, dosing instructions, storage requirements, etc.
                  </p>
                </div>
              ) : (
                <div>
                  {product.productDetails ? (
                    <RichTextDisplay content={product.productDetails} />
                  ) : (
                    <p className="text-gray-500 italic">No additional product details</p>
                  )}
                </div>
              )}
            </div>

            {/* Price Section */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Pricing</h2>
              
              {isAdmin && isEditing ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {getPriceLabel()} ($)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-sm text-gray-600">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editForm.basePrice || 0}
                      onChange={(e) => handleFormChange('basePrice', parseFloat(e.target.value))}
                      className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {getPriceLabel()}
                  </label>
                  {getDisplayPrice() !== null ? (
                    <div className="flex items-center">
                      <DollarSign className="w-5 h-5 text-green-600 mr-1" />
                      <span className="text-2xl font-bold text-gray-900">
                        {getDisplayPrice()!.toFixed(2)}
                      </span>
                      {user?.role === 'admin' && (
                        <span className="text-sm text-blue-600 font-medium ml-2">(wholesale)</span>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <X className="w-5 h-5 text-red-500 mr-1" />
                      <span className="text-sm text-red-600">Pricing unavailable</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Status</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Active</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    product.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {product.isActive ? 'Yes' : 'No'}
                  </span>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Lifecycle</span>
                    {isAdmin && isEditing ? (
                      <select
                        value={editForm.lifecycleStatus || 'active'}
                        onChange={(e) => handleFormChange('lifecycleStatus', e.target.value as ProductLifecycleStatus)}
                        className="text-sm border border-gray-300 rounded-lg px-2 py-1"
                        aria-label="Lifecycle status"
                      >
                        {LIFECYCLE_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    ) : product.lifecycleStatus && product.lifecycleStatus !== 'active' ? (
                      <LifecycleBadge status={product.lifecycleStatus} />
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                    )}
                  </div>
                  {isAdmin && isEditing && (
                    <p className="mt-1 text-xs text-gray-500">
                      {LIFECYCLE_OPTIONS.find(option => option.value === (editForm.lifecycleStatus || 'active'))?.help}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Direct to Patient</span>
                  {isAdmin && isEditing ? (
                    <button
                      type="button"
                      onClick={() => handleFormChange('directToPatient', !editForm.directToPatient)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        editForm.directToPatient ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        editForm.directToPatient ? 'translate-x-5' : 'translate-x-0'
                      }`} />
                    </button>
                  ) : (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      product.directToPatient
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {product.directToPatient ? 'Yes' : 'No'}
                    </span>
                  )}
                </div>

                {/* Membership eligible — whether this product can be picked into
                    a DTP membership tier. */}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Membership Eligible</span>
                  {isAdmin && isEditing ? (
                    <button
                      type="button"
                      onClick={() => handleFormChange('membershipEligible', !editForm.membershipEligible)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        editForm.membershipEligible ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        editForm.membershipEligible ? 'translate-x-5' : 'translate-x-0'
                      }`} />
                    </button>
                  ) : (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      product.membershipEligible
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {product.membershipEligible ? 'Yes' : 'No'}
                    </span>
                  )}
                </div>

                {/* Store category — drives which section of the educational
                    storefront this product appears under (primaryCategorySlug).
                    A product with no category is hidden from the educational
                    (category-browsing) storefront variant. */}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Store Category</span>
                  {isAdmin && isEditing ? (
                    <select
                      value={editForm.primaryCategorySlug || ''}
                      onChange={(e) => handleFormChange('primaryCategorySlug', e.target.value || undefined)}
                      className="text-sm border border-gray-300 rounded-md px-2 py-1 max-w-[14rem] focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">— None —</option>
                      {editForm.primaryCategorySlug &&
                        !dtpCategories.some(c => c.slug === editForm.primaryCategorySlug) && (
                        <option value={editForm.primaryCategorySlug}>{editForm.primaryCategorySlug} (inactive)</option>
                      )}
                      {dtpCategories.map(c => (
                        <option key={c.slug} value={c.slug}>{dtpCategoryLabel(c.slug)}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-sm text-gray-900">{dtpCategoryLabel(product.primaryCategorySlug) || '—'}</span>
                  )}
                </div>

                {/* Product family — groups sibling SKUs (e.g. 5ml + 3ml GHK-Cu)
                    into one family in the educational storefront (drugFamilySlug). */}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Product Family</span>
                  {isAdmin && isEditing ? (
                    <select
                      value={editForm.drugFamilySlug || ''}
                      onChange={(e) => handleFormChange('drugFamilySlug', e.target.value || undefined)}
                      className="text-sm border border-gray-300 rounded-md px-2 py-1 max-w-[14rem] focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">— None —</option>
                      {editForm.drugFamilySlug &&
                        !drugFamilies.some(f => f.slug === editForm.drugFamilySlug) && (
                        <option value={editForm.drugFamilySlug}>{editForm.drugFamilySlug}</option>
                      )}
                      {drugFamilies.map(f => (
                        <option key={f.slug} value={f.slug}>
                          {f.slug}{f.categorySlug ? ` (${f.categorySlug})` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-sm text-gray-900">{product.drugFamilySlug || '—'}</span>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Low Stock Warning</span>
                  {isAdmin && isEditing ? (
                    <button
                      type="button"
                      onClick={() => handleFormChange('lowStockWarning', !editForm.lowStockWarning)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${
                        editForm.lowStockWarning ? 'bg-amber-500' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        editForm.lowStockWarning ? 'translate-x-5' : 'translate-x-0'
                      }`} />
                    </button>
                  ) : (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      product.lowStockWarning
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {product.lowStockWarning ? 'On' : 'Off'}
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Out of Stock</span>
                  {isAdmin && isEditing ? (
                    <button
                      type="button"
                      onClick={() => handleFormChange('outOfStock', !editForm.outOfStock)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                        editForm.outOfStock ? 'bg-red-600' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        editForm.outOfStock ? 'translate-x-5' : 'translate-x-0'
                      }`} />
                    </button>
                  ) : (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      product.outOfStock
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {product.outOfStock ? 'Yes' : 'No'}
                    </span>
                  )}
                </div>

                {isAdmin && id && !isEditing && <CompoundOsReplenishmentPanel productId={id} />}

                {/* Telemed network requirement — gated by per-admin permission.
                    Supplies are always exempt, so the toggle is informational
                    only for supply products. */}
                {canManageTelemed && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">
                    Requires Telemed Network
                    {product.productType === 'supply' && (
                      <span className="block text-xs text-gray-400">Supplies are always exempt</span>
                    )}
                  </span>
                  {isAdmin && isEditing && product.productType !== 'supply' ? (
                    <button
                      type="button"
                      onClick={() => handleFormChange('requiresTelemedNetwork', !editForm.requiresTelemedNetwork)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        editForm.requiresTelemedNetwork ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        editForm.requiresTelemedNetwork ? 'translate-x-5' : 'translate-x-0'
                      }`} />
                    </button>
                  ) : (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      product.requiresTelemedNetwork && product.productType !== 'supply'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {product.requiresTelemedNetwork && product.productType !== 'supply' ? 'Yes' : 'No'}
                    </span>
                  )}
                </div>
                )}

                {/* Prescriber-optional — the supply-like class a customer can
                    simply buy, or fill against a script from their own doctor.
                    Turning this on means the product ships with NO prescriber
                    when the ordering clinic holds no NPI, and never carries the
                    telemed visit fee. Same permission gate as the flag above. */}
                {canManageTelemed && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">
                    Prescriber Optional
                    <span className="block text-xs text-gray-400">
                      {product.productType === 'lab'
                        ? 'Never available for lab tests — the lab requires an ordering NPI'
                        : 'Dispensable with no prescriber'}
                    </span>
                  </span>
                  {isAdmin && isEditing && product.productType !== 'lab' ? (
                    <button
                      type="button"
                      onClick={() => handleFormChange('prescriberOptional', !editForm.prescriberOptional)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        editForm.prescriberOptional ? 'bg-amber-600' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        editForm.prescriberOptional ? 'translate-x-5' : 'translate-x-0'
                      }`} />
                    </button>
                  ) : (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      product.prescriberOptional ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {product.prescriberOptional ? 'Yes' : 'No'}
                    </span>
                  )}
                </div>
                )}

                {isAdmin && <a href="/intake-programs" className="inline-block text-sm text-blue-700 underline">Manage treatment programs, intake questions & routing</a>}
                {isAdmin && <ProductQualificationEditor questions={effectiveQualificationQuestions(isEditing ? { ...product, ...editForm } : product)} onChange={isEditing ? questions => handleFormChange('qualificationQuestions', questions) : undefined} />}

                {/* Fulfillment. A lab panel is filled by the lab vendor, so the
                    peptide-derived pharmacy badge would be actively misleading
                    on one — it is replaced rather than left showing Greenstone. */}
                {product.productType === 'lab' ? (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">
                    Fulfillment
                    <span className="block text-xs text-gray-400">
                      Kit mailed to the patient
                    </span>
                  </span>
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                    Lab vendor
                  </span>
                </div>
                ) : (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">
                    Peptide
                    <span className="block text-xs text-gray-400">
                      Peptides are filled by Blue Five Labs
                    </span>
                  </span>
                  {isAdmin && isEditing ? (
                    <button
                      type="button"
                      onClick={() => handleFormChange('isPeptide', !editForm.isPeptide)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        editForm.isPeptide ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        editForm.isPeptide ? 'translate-x-5' : 'translate-x-0'
                      }`} />
                    </button>
                  ) : (
                    <PharmacyBadge
                      pharmacy={resolveFulfillmentPharmacy([{ isPeptide: product.isPeptide }])}
                      size="sm"
                    />
                  )}
                </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Created</span>
                  <span className="text-sm text-gray-900">
                    {formatShortDate(product.createdAt)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Last Updated</span>
                  <span className="text-sm text-gray-900">
                    {formatShortDate(product.updatedAt)}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Info */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Info</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Package className="w-4 h-4 text-gray-600 mr-2" />
                  <span className="text-sm text-gray-900">{product.format}</span>
                </div>

                {product.budDays && (
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 text-orange-600 mr-2" />
                    <span className="text-sm text-gray-900">BUD: {product.budDays}</span>
                  </div>
                )}

                <div className="flex items-center">
                  <Clock className="w-4 h-4 text-blue-600 mr-2" />
                  <span className="text-sm text-gray-900">{product.estimatedShipDays} days shipping</span>
                </div>

                <div className="flex items-center">
                  <Tag className="w-4 h-4 text-purple-600 mr-2" />
                  <span className="text-sm text-gray-900">{product.categories.length} categories</span>
                </div>

                {product.defaultSig && (
                  <div className="flex items-start pt-2 border-t border-gray-100 mt-2">
                    <FileText className="w-4 h-4 text-green-600 mr-2 mt-0.5" />
                    <div>
                      <span className="text-xs text-gray-500 block">Default Sig</span>
                      <span className="text-sm text-gray-900">{product.defaultSig}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* Audit History Collapsible Section */}
        {isAdmin && (
          <div className="mt-8">
            <div className="bg-white rounded-xl border border-gray-200">
              <div 
                className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setShowHistory(!showHistory)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <History className="w-5 h-5 text-gray-600" />
                    <h2 className="text-xl font-semibold text-gray-900">Audit History</h2>
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                      {history.length} {history.length === 1 ? 'entry' : 'entries'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">
                      {showHistory ? 'Hide' : 'Show'} details
                    </span>
                    {showHistory ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {showHistory && (
                <div className="border-t border-gray-200 p-6">
                  {historyLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : history.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <History className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No History Yet</h3>
                      <p className="text-sm text-gray-500 mb-1">
                        This product hasn't been modified since creation.
                      </p>
                      <p className="text-xs text-gray-400">
                        All changes, updates, and modifications will be tracked here.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {history.map((entry) => (
                        <div key={entry.id} className="border border-gray-200 rounded-lg">
                          <div 
                            className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => toggleHistoryEntry(entry.id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getActionColor(entry.action)}`}>
                                  {entry.action}
                                </span>
                                <div className="flex items-center text-sm text-gray-600">
                                  <User className="w-4 h-4 mr-1" />
                                  {entry.user.email}
                                </div>
                                <div className="flex items-center text-sm text-gray-500">
                                  <Calendar className="w-4 h-4 mr-1" />
                                  {formatDateTime(entry.timestamp)}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-500">
                                  {entry.changes.length} change{entry.changes.length !== 1 ? 's' : ''}
                                </span>
                                {expandedEntries.has(entry.id) ? (
                                  <ChevronUp className="w-4 h-4 text-gray-400" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-gray-400" />
                                )}
                              </div>
                            </div>
                          </div>

                          {expandedEntries.has(entry.id) && (
                            <div className="border-t border-gray-200 p-4 bg-gray-50">
                              <div className="space-y-3">
                                {entry.changes.map((change, index) => (
                                  <div key={index} className="flex justify-between items-start py-2 border-b border-gray-200 last:border-b-0">
                                    <div className="font-medium text-gray-700 capitalize">
                                      {change.field.replace(/([A-Z])/g, ' $1').trim()}
                                    </div>
                                    <div className="text-right">
                                      {entry.action !== 'create' && (
                                        <div className="text-sm text-gray-500 mb-1">
                                          <span className="font-medium">From:</span> {formatValue(change.oldValue)}
                                        </div>
                                      )}
                                      <div className="text-sm text-gray-900">
                                        <span className="font-medium">
                                          {entry.action === 'create' ? 'Set to:' : 'To:'}
                                        </span> {formatValue(change.newValue)}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}


      </div>
    </div>
  );
}
